import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapPin, Loader2 } from 'lucide-react';
import { 
  COUNTRIES, 
  getStatesForCountry, 
  hasStates, 
  lookupZipCode,
  debounce 
} from '@/utils/addressUtils';

/**
 * Reusable Address Form Component
 * Field order: Country -> State -> ZIP Code -> City -> Address
 * Features: State dropdown based on country, ZIP auto-fill
 */
export default function AddressForm({ 
  data, 
  onChange, 
  prefix = '',  // For billing_, shipping_ prefixes
  showLabels = true,
  className = ''
}) {
  const [zipLoading, setZipLoading] = useState(false);
  
  // Helper to get field name with prefix
  const field = (name) => prefix ? `${prefix}${name}` : name;
  
  // Helper to get value - supports both 'zip' and 'zip_code' field names
  const getValue = (name) => data[field(name)] || '';
  const getZipValue = () => data[field('zip')] || data[field('zip_code')] || '';
  
  // Handle field change
  const handleChange = (name, value) => {
    onChange(field(name), value);
  };
  
  // Reset state when country changes
  const handleCountryChange = (value) => {
    handleChange('country', value);
    // Clear state when country changes
    handleChange('state', '');
  };
  
  // ZIP code lookup with debounce
  const performZipLookup = useCallback(
    debounce(async (zipCode, countryCode) => {
      if (!zipCode || zipCode.length < 3) return;
      
      setZipLoading(true);
      try {
        const result = await lookupZipCode(zipCode, countryCode);
        if (result) {
          // Auto-fill city
          if (result.city) {
            handleChange('city', result.city);
          }
          // Auto-fill state (use abbreviation if available)
          if (result.state) {
            handleChange('state', result.state);
          }
        }
      } finally {
        setZipLoading(false);
      }
    }, 500),
    [onChange, prefix]
  );
  
  // Handle ZIP code change
  const handleZipChange = (value) => {
    handleChange('zip', value);
    // Trigger lookup for supported countries
    const country = getValue('country') || 'US';
    if (['US', 'CA', 'DE', 'GB', 'AU', 'BR', 'MX', 'IN'].includes(country)) {
      performZipLookup(value, country);
    }
  };
  
  const country = getValue('country') || 'US';
  const countryHasStates = hasStates(country);
  const states = getStatesForCountry(country);
  
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Row 1: Country */}
      <div className="space-y-2">
        {showLabels && <Label>Country *</Label>}
        <Select 
          value={country} 
          onValueChange={handleCountryChange}
        >
          <SelectTrigger className="rounded-lg" data-testid={`${prefix}country-select`}>
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Row 2: State and ZIP Code */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          {showLabels && <Label>State / Province {countryHasStates ? '*' : ''}</Label>}
          {countryHasStates ? (
            <Select 
              value={getValue('state')} 
              onValueChange={(value) => handleChange('state', value)}
            >
              <SelectTrigger className="rounded-lg" data-testid={`${prefix}state-select`}>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {states.map((state) => (
                  <SelectItem key={state.code} value={state.code}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={getValue('state')}
              onChange={(e) => handleChange('state', e.target.value)}
              className="rounded-lg"
              placeholder="State/Province"
              data-testid={`${prefix}state-input`}
            />
          )}
        </div>
        <div className="space-y-2">
          {showLabels && <Label>ZIP / Postal Code *</Label>}
          <div className="relative">
            <Input
              value={getZipValue()}
              onChange={(e) => handleZipChange(e.target.value)}
              className="rounded-lg pr-8"
              placeholder="ZIP / Postal Code"
              data-testid={`${prefix}zip-input`}
            />
            {zipLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
            )}
          </div>
          <p className="text-xs text-slate-400">Enter ZIP to auto-fill city & state</p>
        </div>
      </div>
      
      {/* Row 3: City */}
      <div className="space-y-2">
        {showLabels && <Label>City *</Label>}
        <Input
          value={getValue('city')}
          onChange={(e) => handleChange('city', e.target.value)}
          className="rounded-lg"
          placeholder="City"
          data-testid={`${prefix}city-input`}
        />
      </div>
      
      {/* Row 4: Street Address */}
      <div className="space-y-2">
        {showLabels && <Label>Street Address *</Label>}
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={getValue('address')}
            onChange={(e) => handleChange('address', e.target.value)}
            className="pl-9 rounded-lg"
            placeholder="123 Main Street, Suite 100"
            data-testid={`${prefix}address-input`}
          />
        </div>
      </div>
    </div>
  );
}
